// src/components/Admin/Logs/LogsScreen.jsx
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Menu,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import Colors from "../../../assets/Variables/ColorsVariables";
import * as Styles from "../Accounts/Styles.js";
import SearchBoxComponent from "../../Generals/SearchBox/SearchBoxComponent";
import Button3px from "../../Generals/Buttons/3pxButton";
import NotificationModal from "../../Generals/Notifications/NotificationModal";
import TableFrontPagination from "../../Generals/Tables/TableFrontPagination";
import IconComponent from "../../Generals/Icons/IconComponent";
import TitledSelectorSlim from "../../Generals/Selectors/TitledSelectorSlim";
import DateSelector from "../../Generals/Selectors/DateSelector";
import StatusChip from "../../Generals/Chips/StatusChip";
import ExportComponent from "../../Generals/ExporterFiles/ExporterFileComponent";
import adminMethods from "../../../services/admin";

// =====================
// Constants
// =====================

// Mapping of LOG_CODE values to more user-friendly labels
const LOG_LABEL_MAP = {
  "INFO": "INFORMATION",
  "SUCCESS": "SUCCESSFUL",
  "ERROR": "ERROR",
  "WARNING": "WARNING",
};

// Time period options
const DATE_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This week", value: "This week" },
  { label: "This month", value: "This month" },
  { label: "This year", value: "This year" },
  { label: "Custom Period", value: "Custom" },
];

// Log Codes Options
const LOG_CODES_OPTIONS = [
  { label: "All", value: "" },
  { label: "Successful", value: "SUCCESSFUL" },
  { label: "Error", value: "ERROR" },
  { label: "Warning", value: "WARNING" },
  { label: "Information", value: "INFO" },
];

// Log Types Options
const LOG_TYPES_OPTIONS = [
  { label: "All", value: "" },
  { label: "Connections", value: "CONNECTION" },
  { label: "Copies", value: "COPIES" },
  { label: "Events", value: "EVENTS" },
  { label: "Requests", value: "REQUESTS" },
];

// Chips
const LogStatusChip = ({ value }) => <StatusChip value={value} />;

const LogTypeChip = ({ value }) => (
  <StatusChip
    value={value}
    sx={{
      fontSize: "11px",
      padding: "2px 8px",
      minHeight: "24px",
    }}
  />
);

// Maps LOG_CODE to visual status for the chip
const mapCodeToStatus = (code) => {
  if (!code) return "info";
  const upper = code.toString().toUpperCase();
  if (upper.includes("SUCCESS")) return "success";
  if (upper.includes("ERROR") || upper.includes("FAIL")) return "error";
  if (upper.includes("WARNING")) return "warning";
  return "info";
};

// Columns for the logs table
const LOGS_COLUMNS = [
  {
    field: "code",
    headerName: "Status",
    flex: 1,
    renderCell: (params) => <LogStatusChip value={params.value} />,
  },
  {
    field: "type",
    headerName: "Type",
    flex: 1.2,
    renderCell: (params) => <LogTypeChip value={params.value} />,
  },
  { field: "date", headerName: "Date", flex: 1.2 },
  { field: "origin", headerName: "Origin", flex: 1 },
  {
    field: "message",
    headerName: "Description",
    flex: 2.5,
    sortable: false,
    filterable: false,
  },
];

const LogsScreen = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const initialPresetSearch = location.state?.presetSearch ?? "";
  const shouldAutoSearch = Boolean(location.state?.autoSearch);
  const hasExecutedPresetSearch = useRef(false);
  const [searchTerm, setSearchTerm] = useState(initialPresetSearch);
  const [dateSelect, setDateSelect] = useState("Today");
  const [logCodeSelect, setLogCodeSelect] = useState("");
  const [logTypeSelect, setLogTypeSelect] = useState("");
  const [showDatePickers, setShowDatePickers] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [allLogsRows, setAllLogsRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  const [modalConfig, setModalConfig] = useState({
    open: false,
    iconType: "info",
    title: "",
    message: "",
    onAcceptLabel: "OK",
    onRefuseLabel: undefined,
    onContinueLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
    onContinue: undefined,
    onClose: () => setModalConfig((prev) => ({ ...prev, open: false })),
  });

  // =====================
  // Helpers of dates and filters
  // =====================

  const toUnix = (date) => (date ? Math.floor(date.getTime() / 1000) : null);

  const buildFilters = () => {
    let from = null;
    let to = null;
    const now = new Date();

    switch (dateSelect) {
      case "Today": {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        from = toUnix(start);
        to = toUnix(end);
        break;
      }
      case "This week": {
        const start = new Date();
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        from = toUnix(start);
        to = toUnix(end);
        break;
      }
      case "This month": {
        const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        const end = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        from = toUnix(start);
        to = toUnix(end);
        break;
      }
      case "This year": {
        const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        const end = new Date(
          now.getFullYear(),
          11,
          31,
          23,
          59,
          59,
          999
        );
        from = toUnix(start);
        to = toUnix(end);
        break;
      }
      case "Custom": {
        from = toUnix(fromDate);
        to = toUnix(toDate);
        break;
      }
      default:
        break;
    }

    return {
      search: searchTerm || null,
      code: logCodeSelect || null,
      type: logTypeSelect || null,
      from,
      to,
    };
  };

  // =====================
  // Export handlers
  // =====================

  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);

  const handleExport = (type) => {
    const today = new Date();
    const formattedDate = today.toISOString().slice(0, 10).replace(/-/g, "");

    const exportColumns = LOGS_COLUMNS.filter((c) => c.field !== "actions");

    if (type === "csv") {
      ExportComponent.GenerateCSV(exportColumns, allLogsRows, `logs_history_${formattedDate}`);
    }
    if (type === "pdf") {
      ExportComponent.GeneratePDF(exportColumns, allLogsRows, `logs_history_${formattedDate}`);
    }
    if (type === "txt") {
      ExportComponent.GenerateTXT(exportColumns, allLogsRows, `logs_history_${formattedDate}`);
    }

    handleExportClose();
  };

  // =====================
  // Request real to backend
  // =====================

  const RequestLogs = async () => {
    try {
      setLoading(true);
      const filters = buildFilters();

      if (!filters.from || !filters.to) {
        setModalConfig((prev) => ({
          ...prev,
          open: true,
          iconType: "warning",
          title: "Invalid dates",
          message: "You must select a valid period of time (from / to) for the search.",
        }));
        setAllLogsRows([]);
        return;
      }

      if (filters.from > filters.to) {
        setModalConfig((prev) => ({
          ...prev,
          open: true,
          iconType: "warning",
          title: "Invalid range",
          message: "The 'From' date cannot be greater than the 'To' date.",
        }));
        setAllLogsRows([]);
        return;
      }

      const params = {
        search: filters.search ?? undefined,
        code: filters.code ?? undefined,
        type: filters.type ?? undefined,
        from: filters.from,
        to: filters.to,
      };

      const res = await adminMethods.getAdminLogs(params);
      const { status, data, message } = res.data || {};

      if (status !== "OK" && status !== "Success" && status !== 200) {
        setModalConfig((prev) => ({
          ...prev,
          open: true,
          iconType: "error",
          title: "Error loading logs",
          message: message || "Unexpected error while fetching logs.",
        }));
        setAllLogsRows([]);
        return;
      }

      const messages = data?.messages || [];

      const rows = messages.map((item, index) => ({
        id: index,
        // IMPLEMENTATION OPTION 1: Map INFO to INFORMATION before saving to the state
        code: LOG_LABEL_MAP[item.code?.toUpperCase()] || item.code,
        status: mapCodeToStatus(item.code),
        type: item.type,
        date: item.date,
        origin: item.origin,
        message: item.message,
      }));

      setAllLogsRows(rows);
    } catch (error) {
      console.error("Error fetching logs:", error);
      const backendMessage = error?.response?.data?.message || "There was an error contacting the logs service.";
      setModalConfig((prev) => ({
        ...prev,
        open: true,
        iconType: "error",
        title: "Server error",
        message: backendMessage,
      }));
      setAllLogsRows([]);
    } finally {
      setLoading(false);
    }
  };

  // =====================
  // Handlers UI
  // =====================

  const handleClearFilters = () => {
    setSearchTerm("");
    setDateSelect("Today");
    setLogCodeSelect("");
    setLogTypeSelect("");
    setShowDatePickers(false);
    setFromDate(null);
    setToDate(null);
  };

  useEffect(() => {
    if (shouldAutoSearch && initialPresetSearch && !hasExecutedPresetSearch.current) {
      hasExecutedPresetSearch.current = true;
      RequestLogs();
      return;
    }

    if (!hasExecutedPresetSearch.current) {
      hasExecutedPresetSearch.current = true;
      RequestLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.state?.presetSearch) {
      setSearchTerm(String(location.state.presetSearch));
    }
  }, [location.state]);

  const handleDateSelector = (event) => {
    const value = event.target.value;
    setDateSelect(value);
    if (value === "Custom") {
      setShowDatePickers(true);
    } else {
      setShowDatePickers(false);
    }
  };

  const handleSearchClick = () => {
    RequestLogs();
  };

  return (
    <Paper elevation={2} sx={Styles.containerPaper}>
      <NotificationModal {...modalConfig} />

      <Typography variant="h5" gutterBottom>
        Logs History
      </Typography>
      <Typography sx={{ fontSize: "15px", color: "#687B8E", mb: 3 }}>
        Monitor system activity, errors, warnings and security events across all services.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: "#F8FAFC",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: 2.5,
              color: Colors.Secondary_700,
              marginTop: "-10px",
            }}
          >
            Search
          </Typography>
          <SearchBoxComponent
            value={searchTerm}
            handleSearchTerm={(e) => setSearchTerm(e.target.value)}
            placeholderprops={"Search by code, description or origin"}
            variant="statements"
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "11px",
              lineHeight: "140%",
              color: Colors.Secondary_700,
            }}
          >
            Log Code
          </Typography>
          <TitledSelectorSlim
            title=""
            value={logCodeSelect}
            handleChange={(e) => setLogCodeSelect(e.target.value)}
            options={LOG_CODES_OPTIONS}
            variant="statements"
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "11px",
              lineHeight: "140%",
              color: Colors.Secondary_700,
            }}
          >
            Log Type
          </Typography>
          <TitledSelectorSlim
            title=""
            value={logTypeSelect}
            handleChange={(e) => setLogTypeSelect(e.target.value)}
            options={LOG_TYPES_OPTIONS}
            variant="statements"
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "11px",
              lineHeight: "140%",
              color: Colors.Secondary_700,
            }}
          >
            Period of time
          </Typography>
          <TitledSelectorSlim
            title=""
            value={dateSelect}
            handleChange={handleDateSelector}
            options={DATE_OPTIONS}
            variant="statements"
          />
        </Box>

        {showDatePickers && (
          <>
            <DateSelector
              title="From"
              value={fromDate}
              handleChange={setFromDate}
              variant="statements"
            />
            <DateSelector
              title="To"
              value={toDate}
              handleChange={setToDate}
              variant="statements"
            />
          </>
        )}

        <Box
          sx={{
            marginTop: "17px",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            ml: { xs: 0, md: 0 },
          }}
        >
          <Button
            variant="outlined"
            sx={{
              minWidth: 40,
              padding: "12px 10px",
              borderRadius: "8px",
              borderColor: Colors.Secondary_50,
              color: Colors.Gray_600,
              "&:hover": {
                borderColor: Colors.Secondary_100,
                backgroundColor: Colors.Gray_50,
              },
            }}
            onClick={handleClearFilters}
          >
            <IconComponent icon="loop" size="16px" />
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans, sans-serif",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: 1.4,
                textAlign: "center",
                color: Colors.Gray_600,
              }}
            >
              Clear
            </Typography>
          </Button>

          <Button3px
            label={"Search"}
            color={Colors.Warning_50}
            backgroundColor={Colors.Error_500}
            type="submit"
            HandleOnClick={handleSearchClick}
            height="40px"
            disabled={dateSelect === "Custom" && (!fromDate || !toDate)}
          >
            Search
          </Button3px>
        </Box>

        <Box
          sx={{
            marginTop: "17px",
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            sx={Styles.ExportButton}
            onClick={handleExportClick}
          >
            <IconComponent icon="download" size="16px" />
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans, sans-serif",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              Export
            </Typography>
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleExportClose}
          >
            <MuiMenuItem onClick={() => handleExport("csv")}>Export as CSV</MuiMenuItem>
            <MuiMenuItem onClick={() => handleExport("pdf")}>Export as PDF</MuiMenuItem>
            <MuiMenuItem onClick={() => handleExport("txt")}>Export as TXT</MuiMenuItem>
          </Menu>
        </Box>
      </Box>

      <Box style={{ height: 650, width: "100%", marginTop: "0px" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableFrontPagination rows={allLogsRows} columns={LOGS_COLUMNS} />
        )}
      </Box>
    </Paper>
  );
};

export default LogsScreen;